import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearch } from 'wouter';
import { Navbar } from '@/components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useVerifyCollectCheckout } from '@/hooks/usePayment';

export default function CheckoutSuccess() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const { refetch: refetchUser } = useAuth();
  const { toast } = useToast();
  const verifyCheckout = useVerifyCollectCheckout();

  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [countdown, setCountdown] = useState(5);
  const hasVerified = useRef(false);

  useEffect(() => {
    if (hasVerified.current) {
      return;
    }

    const params = new URLSearchParams(search);
    const transactionId = params.get('transaction_id');
    const vaultId = params.get('vault_id');
    const planId = localStorage.getItem('pendingCheckoutPlanId');

    if (!transactionId || !vaultId || !planId) {
      setStatus('error');
      setErrorMessage('Missing checkout information. Please try again.');
      return;
    }

    hasVerified.current = true;

    verifyCheckout.mutate(
      { transactionId, vaultId, planId },
      {
        onSuccess: () => {
          setStatus('success');
          localStorage.removeItem('pendingCheckoutPlanId');
          refetchUser();
          toast({
            title: 'Subscription activated!',
            description: 'Your plan is now active.',
          });
        },
        onError: (error) => {
          setStatus('error');
          setErrorMessage(error instanceof Error ? error.message : 'Failed to verify payment');
          localStorage.removeItem('pendingCheckoutPlanId');
        },
      }
    );
  }, [search, verifyCheckout, refetchUser, toast]);

  useEffect(() => {
    if (status !== 'success' || countdown <= 0) {
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown, status]);

  useEffect(() => {
    if (status === 'success' && countdown <= 0) {
      setLocation('/account');
    }
  }, [countdown, status, setLocation]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-20 pb-10 px-4 sm:px-6">
        <div className="max-w-xl mx-auto">
          <Card className="glass">
            <CardHeader className="text-center">
              {status === 'verifying' && (
                <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <Loader2 className="h-8 w-8 text-primary animate-spin" />
                </div>
              )}
              {status === 'success' && (
                <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
              )}
              {status === 'error' && (
                <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
                  <XCircle className="h-8 w-8 text-destructive" />
                </div>
              )}

              <CardTitle className="text-xl">
                {status === 'verifying' && 'Verifying Payment...'}
                {status === 'success' && 'Welcome to Pro!'}
                {status === 'error' && 'Payment Verification Failed'}
              </CardTitle>
              <CardDescription>
                {status === 'verifying' && 'Please wait while we confirm your subscription'}
                {status === 'success' && 'Your subscription has been activated successfully'}
                {status === 'error' && errorMessage}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4 text-center">
              {status === 'success' && (
                <>
                  <p className="text-sm text-muted-foreground">
                    You now have access to all Pro features including premium daily tips, in-depth match analysis, and priority support.
                  </p>
                  <p className="text-sm font-medium">
                    Redirecting to your account in {countdown}...
                  </p>
                </>
              )}

              {status === 'error' && (
                <p className="text-sm text-muted-foreground">
                  If you completed payment, your subscription may still be processing. Please check your account or contact support.
                </p>
              )}

              <Button className="w-full" onClick={() => setLocation('/account')}>
                Go to Account
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
